import { useCallback, useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "@/App.css";
import { CONFIG } from "@/config";
import { Header } from "@/components/Header";
import { RegistrationGate } from "@/components/RegistrationGate";
import { RegistrationDialog } from "@/components/RegistrationDialog";
import { VideoStage } from "@/components/VideoStage";
import { NextSteps } from "@/components/NextSteps";
import { StickyBar } from "@/components/StickyBar";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { getUtm, postToSheet } from "@/lib/sheets";
import { loadSession, saveSession } from "@/lib/session";

/*
  Two states. A refresh in the same tab within 5 hours keeps the viewer (sessionStorage);
  a new tab, link click or device always starts at the form.
  1. viewer === null -> registration gate (no video in the DOM)
  2. viewer set      -> video + next-step cards + brochure button + sticky bar
*/
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path={CONFIG.PAGE_PATH} element={<WebinarPage />} />
        <Route path="*" element={<Navigate to={CONFIG.PAGE_PATH} replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function WebinarPage() {
  const [viewer, setViewer] = useState(() => loadSession());
  const [showRegister, setShowRegister] = useState(false);
  const [watchedSeconds, setWatchedSeconds] = useState(0);
  const [activeOption, setActiveOption] = useState(null);
  const lenisRef = useRef(null);
  const nextStepsRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ autoRaf: true, lerp: 0.1 });
    lenisRef.current = lenis;
    return () => lenis.destroy();
  }, []);

  const onProgress = useCallback((seconds) => setWatchedSeconds(seconds), []);

  const scrollToNextSteps = () => {
    if (nextStepsRef.current) lenisRef.current?.scrollTo(nextStepsRef.current, { offset: -24 });
  };

  const minutesWatched = Math.floor(watchedSeconds / 60);

  // Cards with a URL log the enquiry silently and open the link in a new tab
  const selectOption = (option) => {
    if (option.url) {
      window.open(option.url, "_blank", "noopener,noreferrer");
      postToSheet({
        type: "enquiry",
        option: option.title,
        name: viewer.name,
        phone: viewer.phone,
        email: viewer.email,
        message: "",
        minutesWatched,
        leadId: viewer.leadId,
        ...getUtm(),
      }).catch(() => {});
      return;
    }
    setActiveOption(option);
  };

  // Opens the brochure and marks this lead's row in the leads sheet
  const downloadBrochure = () => {
    window.open(CONFIG.brochure.file, "_blank", "noopener,noreferrer");
    postToSheet({
      type: "brochure",
      name: viewer.name,
      phone: viewer.phone,
      email: viewer.email,
      leadId: viewer.leadId,
      minutesWatched,
    }).catch(() => {});
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#0B0C0A] text-[#F4F5F2]">
      <div className="grid-bg" aria-hidden="true" />
      <div className="orb orb-lime" aria-hidden="true" />
      <div className="orb orb-gold" aria-hidden="true" />

      <main className={`relative mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${viewer ? "pb-28" : "pb-16"}`}>
        <Header badge={CONFIG.gate.badge} />

        <AnimatePresence mode="wait">
          {!viewer ? (
            <motion.div key="gate" exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.45 }}>
              <RegistrationGate onPlay={() => setShowRegister(true)} />
            </motion.div>
          ) : (
            <motion.div key="video" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <VideoStage viewer={viewer} onProgress={onProgress} />
              <NextSteps ref={nextStepsRef} onSelect={selectOption} onBrochure={downloadBrochure} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {!viewer && (
        <RegistrationDialog
          open={showRegister}
          onClose={() => setShowRegister(false)}
          onRegistered={(v) => { saveSession(v, getUtm()); setShowRegister(false); setViewer(v); }}
        />
      )}

      {viewer && <StickyBar onClick={scrollToNextSteps} />}

      {viewer && (
        <EnquiryDialog
          option={activeOption}
          viewer={viewer}
          minutesWatched={minutesWatched}
          onClose={() => setActiveOption(null)}
        />
      )}
    </div>
  );
}
